/**
 * SGPCard.jsx - Dedicated component for Same Game Parlay display
 * 
 * Unlike LegBetCard (designed for individual straight bets), this component
 * displays multi-leg parlays in a unified, intuitive format.
 */

import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking, Image, Platform } from 'react-native';
import { getSportsbookLogo, getTeamLogo } from '../data/logos';

// Format American odds for display
const formatOdds = (price) => {
    if (price === null || price === undefined || isNaN(price)) return '—';
    const num = Number(price);
    if (num > 0) return `+${num.toLocaleString()}`;
    return num.toLocaleString();
};

// Calculate implied probability from American odds
const getImpliedProbability = (price) => {
    if (price === null || price === undefined || isNaN(price)) return null;
    const num = Number(price);
    if (num > 0) return 100 / (num + 100);
    return Math.abs(num) / (Math.abs(num) + 100);
};

// Calculate potential payout
const calculatePayout = (stake, price) => {
    if (price === null || price === undefined || isNaN(price)) return null;
    const num = Number(price);
    if (num > 0) return stake * (num / 100) + stake;
    return stake * (100 / Math.abs(num)) + stake;
};

const SGPCard = ({ 
    sgp, 
    isSelected = false, 
    onSelect,
    onAddToBetSlip,
    unitSize = 10 
}) => {
    const [stake, setStake] = useState(unitSize);
    const [expanded, setExpanded] = useState(false);
    
    // Extract data from SGP
    const legs = sgp?.legs || sgp?._sgpData?.legs || [];
    const combinedPrice = sgp?.combined_price || sgp?._sgpData?.combined_price || sgp?.price || 0;
    const fairPrice = sgp?.fair_price || sgp?._sgpData?.fair_price || combinedPrice;
    const bestBook = sgp?.best_book || sgp?._sgpData?.best_book || sgp?.sportsbook || 'Unknown';
    const evPercent = sgp?.best_ev || sgp?.ev_percent || sgp?._sgpData?.best_ev || 0;
    const evSource = sgp?.ev_source || sgp?._sgpData?.ev_source || 'none';
    const legEVs = sgp?.leg_evs || sgp?._sgpData?.leg_evs || null;
    const deepLink = sgp?.deep_link || sgp?._sgpData?.deep_link;
    const homeTeam = sgp?.home_team || '';
    const awayTeam = sgp?.away_team || '';
    const legCount = legs.length || sgp?.leg_count || 2;
    const allBooksCount = sgp?.all_books?.length || sgp?._sgpData?.all_books?.length || (sgp?.availableBooks?.length) || 1;
    
    // Get logos
    const sportsbookLogo = getSportsbookLogo(bestBook);
    const homeTeamLogo = getTeamLogo(homeTeam);
    const awayTeamLogo = getTeamLogo(awayTeam);
    
    // Calculations
    const impliedProb = getImpliedProbability(combinedPrice);
    const payout = calculatePayout(stake, combinedPrice);
    
    // Handle bet placement
    const handlePlaceBet = () => {
        if (deepLink) {
            if (Platform.OS === 'web') {
                window.open(deepLink, '_blank');
            } else {
                Linking.openURL(deepLink);
            }
        }
    };
    
    return (
        <TouchableOpacity 
            style={[styles.card, isSelected && styles.cardSelected]}
            onPress={() => onSelect?.(sgp)}
            activeOpacity={0.8}
        >
            {/* Header: Game Info + Sportsbook */}
            <View style={styles.header}>
                <View style={styles.gameInfo}>
                    {/* Team logos */}
                    <View style={styles.teamsContainer}>
                        {awayTeamLogo && (
                            <Image source={{ uri: awayTeamLogo }} style={styles.teamLogo} />
                        )}
                        <Text style={styles.vsText}>@</Text>
                        {homeTeamLogo && (
                            <Image source={{ uri: homeTeamLogo }} style={styles.teamLogo} />
                        )}
                    </View>
                    <View style={styles.teamNames}>
                        <Text style={styles.teamText} numberOfLines={1}>
                            {awayTeam} @ {homeTeam}
                        </Text>
                    </View>
                </View>
                
                {/* Sportsbook badge */}
                <View style={styles.sportsbookBadge}>
                    {sportsbookLogo && (
                        <Image source={{ uri: sportsbookLogo }} style={styles.sportsbookLogo} />
                    )}
                    <Text style={styles.sportsbookName}>{bestBook}</Text>
                </View>
            </View>
            
            {/* SGP Badge */}
            <View style={styles.sgpBadge}>
                <Text style={styles.sgpBadgeText}>🎯 {legCount}-Leg Same Game Parlay</Text>
            </View>
            
            {/* Legs Section */}
            <View style={styles.legsContainer}>
                <Text style={styles.legsTitle}>Parlay Legs:</Text>
                {legs.map((leg, index) => {
                    const legName = leg.name || leg.selection || '';
                    const legMarket = leg.market_name || leg.market || '';
                    const legPrice = leg.price;
                    const legEV = leg.ev_percent;
                    
                    return (
                        <View key={index} style={styles.legRow}>
                            <View style={styles.legNumber}>
                                <Text style={styles.legNumberText}>{index + 1}</Text>
                            </View>
                            <View style={styles.legDetails}>
                                <Text style={styles.legName} numberOfLines={2}>{legName}</Text>
                                <View style={styles.legMetaRow}>
                                    <Text style={styles.legMarket}>{legMarket}</Text>
                                    {legEV != null && legEV !== 0 && (
                                        <Text style={[
                                            styles.legEV,
                                            legEV > 0 ? styles.evPositive : styles.evNegative
                                        ]}>
                                            {legEV > 0 ? '+' : ''}{legEV.toFixed(1)}%
                                        </Text>
                                    )}
                                </View>
                            </View>
                            {legPrice && (
                                <Text style={[
                                    styles.legPrice,
                                    legPrice > 0 ? styles.pricePositive : styles.priceNegative
                                ]}>
                                    {formatOdds(legPrice)}
                                </Text>
                            )}
                        </View>
                    );
                })}
            </View>
            
            {/* Combined Odds & Stats */}
            <View style={styles.statsContainer}>
                <View style={styles.statBox}>
                    <Text style={styles.statLabel}>Combined Odds</Text>
                    <Text style={[
                        styles.statValue,
                        styles.oddsValue,
                        combinedPrice > 0 ? styles.pricePositive : styles.priceNegative
                    ]}>
                        {formatOdds(combinedPrice)}
                    </Text>
                    <Text style={[
                        styles.statSubtext,
                        impliedProb !== null && impliedProb < 0.01 && styles.longShotWarning
                    ]}>
                        {impliedProb !== null 
                            ? impliedProb < 0.01 
                                ? `⚠️ ${(impliedProb * 100).toFixed(2)}% chance` 
                                : `${(impliedProb * 100).toFixed(1)}% implied`
                            : '—'}
                    </Text>
                </View>
                
                <View style={styles.statBox}>
                    <Text style={styles.statLabel}>EV%</Text>
                    <Text style={[
                        styles.statValue,
                        evPercent > 0 ? styles.evPositive : evPercent < 0 ? styles.evNegative : styles.evNeutral
                    ]}>
                        {evPercent !== 0 ? (evPercent >= 0 ? '+' : '') + evPercent.toFixed(2) + '%' : '—'}
                    </Text>
                    <Text style={styles.statSubtext}>
                        {evPercent === 0 
                            ? (allBooksCount <= 1 ? '1 book only' : 'N/A')
                            : evSource === 'leg_combination' 
                                ? 'From leg EVs' 
                                : evSource === 'api' 
                                    ? 'API calculated' 
                                    : 'Expected Value'}
                    </Text>
                </View>
                
                <View style={styles.statBox}>
                    <Text style={styles.statLabel}>${stake} Wins</Text>
                    <Text style={[styles.statValue, styles.payoutValue]}>
                        ${payout ? (payout - stake).toFixed(2) : '—'}
                    </Text>
                    <Text style={styles.statSubtext}>
                        Total: ${payout?.toFixed(2) || '—'}
                    </Text>
                </View>
            </View>
            
            {/* Action Buttons */}
            <View style={styles.actionsContainer}>
                {deepLink && (
                    <TouchableOpacity 
                        style={styles.placeBetButton}
                        onPress={handlePlaceBet}
                    >
                        <Text style={styles.placeBetText}>
                            🎰 Place Bet on {bestBook}
                        </Text>
                    </TouchableOpacity>
                )}
                
                {onAddToBetSlip && (
                    <TouchableOpacity 
                        style={styles.addToSlipButton}
                        onPress={() => onAddToBetSlip(sgp)}
                    >
                        <Text style={styles.addToSlipText}>+ Add to Slip</Text>
                    </TouchableOpacity>
                )}
            </View>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    card: {
        backgroundColor: 'rgba(30, 30, 40, 0.95)',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: 'rgba(162, 89, 255, 0.3)',
        padding: 16,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        // Full width like LegBetCard
        width: '100%',
        alignSelf: 'stretch',
    },
    cardSelected: {
        borderColor: 'rgba(162, 89, 255, 0.8)',
        borderWidth: 3,
        shadowColor: 'rgba(162, 89, 255, 0.6)',
        shadowOpacity: 1,
        shadowRadius: 12,
    },
    
    // Header
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255, 255, 255, 0.1)',
    },
    gameInfo: {
        flex: 1,
    },
    teamsContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 4,
    },
    teamLogo: {
        width: 28,
        height: 28,
        borderRadius: 14,
    },
    vsText: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: 12,
        marginHorizontal: 6,
    },
    teamNames: {
        flexDirection: 'row',
    },
    teamText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 13,
        fontWeight: '500',
    },
    sportsbookBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(162, 89, 255, 0.2)',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: 'rgba(162, 89, 255, 0.4)',
    },
    sportsbookLogo: {
        width: 20,
        height: 20,
        borderRadius: 10,
        marginRight: 6,
    },
    sportsbookName: {
        color: '#a259ff',
        fontSize: 12,
        fontWeight: '600',
    },
    
    // SGP Badge
    sgpBadge: {
        backgroundColor: 'rgba(74, 222, 128, 0.15)',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        marginBottom: 12,
        alignSelf: 'flex-start',
        borderWidth: 1,
        borderColor: 'rgba(74, 222, 128, 0.3)',
    },
    sgpBadgeText: {
        color: '#4ADE80',
        fontSize: 13,
        fontWeight: '600',
    },
    
    // Legs
    legsContainer: {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: 12,
        padding: 12,
        marginBottom: 12,
    },
    legsTitle: {
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: 11,
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 10,
    },
    legRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255, 255, 255, 0.05)',
    },
    legNumber: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: 'rgba(162, 89, 255, 0.3)',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 10,
    },
    legNumberText: {
        color: '#a259ff',
        fontSize: 12,
        fontWeight: '700',
    },
    legDetails: {
        flex: 1,
    },
    legName: {
        color: '#fff',
        fontSize: 14,
        fontWeight: '500',
        marginBottom: 2,
    },
    legMarket: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: 11,
    },
    legMetaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    legEV: {
        fontSize: 10,
        fontWeight: '600',
        paddingHorizontal: 4,
        paddingVertical: 1,
        borderRadius: 3,
        overflow: 'hidden',
    },
    legPrice: {
        fontSize: 13,
        fontWeight: '600',
        marginLeft: 8,
    },
    
    // Stats
    statsContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 16,
    },
    statBox: {
        flex: 1,
        alignItems: 'center',
        paddingHorizontal: 8,
    },
    statLabel: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: 10,
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    statValue: {
        fontSize: 18,
        fontWeight: '700',
        marginBottom: 2,
    },
    statSubtext: {
        color: 'rgba(255, 255, 255, 0.4)',
        fontSize: 10,
    },
    longShotWarning: {
        color: '#FF9500',
        fontWeight: '600',
    },
    oddsValue: {
        color: '#fff',
    },
    payoutValue: {
        color: '#4ADE80',
    },
    
    // Price colors
    pricePositive: {
        color: '#4ADE80',
    },
    priceNegative: {
        color: '#FF6B6B',
    },
    evPositive: {
        color: '#4ADE80',
    },
    evNegative: {
        color: '#FF6B6B',
    },
    evNeutral: {
        color: 'rgba(255, 255, 255, 0.5)',
    },
    
    // Actions
    actionsContainer: {
        flexDirection: 'row',
        gap: 10,
    },
    placeBetButton: {
        flex: 2,
        backgroundColor: '#a259ff',
        paddingVertical: 14,
        borderRadius: 10,
        alignItems: 'center',
    },
    placeBetText: {
        color: '#fff',
        fontSize: 14,
        fontWeight: '700',
    },
    addToSlipButton: {
        flex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        paddingVertical: 14,
        borderRadius: 10,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    addToSlipText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 13,
        fontWeight: '600',
    },
});

export default SGPCard;

